import {
  ButtonMenu,
  Css,
  FullBleed,
  Palette,
  RouteTabWithContent,
  Tabs,
  Tooltip,
  useBreakpoint,
  useTestIds,
} from "@homebound/beam";
import { sentenceCase } from "change-case";
import { MutableRefObject, useCallback } from "react";
import { useLocation } from "react-router";
import { Link, matchPath } from "react-router-dom";
import { useUpdateProjectCoverImageMutation } from "src/components/navigation/CoverImage.generated";
import { StatusIndicator } from "src/components/StatusIndicator";
import { ImageUploader, PendingAsset } from "src/components/uploader/ImageUploader";
import { LotType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeaderBreadcrumbs } from "src/routes/layout/PageHeader";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { ProjectParams } from "src/routes/routesDef";
import { createDevelopmentUrl, createProjectDashboardUrl, createProjectSettingsUrl } from "src/RouteUrls";
import { projectDashboardRoute } from "src/routing/domains/projectsPaths";
import { stageCodeToNameMapper } from "src/utils";
import { projectStatusDetails } from "src/utils/mappers";
import { BooleanParam, useQueryParams } from "use-query-params";
import { createHierarchyBreadcrumbs } from "../../ProjectHeader";

type DashboardHeaderProps = { tabs: RouteTabWithContent[]; headerActionsRef: MutableRefObject<HTMLDivElement | null> };
export function DashboardHeader(props: DashboardHeaderProps) {
  const { tabs, headerActionsRef } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const {
    name,
    market,
    development,
    status,
    latestActiveStage,
    coverImage,
    lotType,
    hopUrl,
    propcoUrl,
    communityType,
    __typename,
    parentsRecursive,
    siblingProjects,
  } = useProjectContext();
  const tid = useTestIds({});
  const location = useLocation();
  const [{ edit }, setQueryParams] = useQueryParams({ edit: BooleanParam });
  const [updateProjectCoverImage] = useUpdateProjectCoverImageMutation();
  const { sm } = useBreakpoint();

  // Create a list of our items, so that we can `.map` them into elements & conditionally add dividers between them.
  const items = [
    market ? (
      <span key="market" {...tid.market}>
        <Tooltip title="Market" placement="top">
          {market.name}
        </Tooltip>
      </span>
    ) : undefined,
    development ? (
      <Link key="development" to={createDevelopmentUrl(development.id)} {...tid.development}>
        <Tooltip title="Development" placement="top">
          {development.name}
        </Tooltip>
      </Link>
    ) : undefined,
    communityType && __typename !== "StandaloneProject" ? (
      <span key="communityType" {...tid.communityType}>
        <Tooltip title="Community Type" placement="top">
          {sentenceCase(communityType)}
        </Tooltip>
      </span>
    ) : undefined,
    <span key="lotType" {...tid.lotType}>
      <Tooltip title="Lot Type" placement="top">
        {lotType}
      </Tooltip>
    </span>,
    <span key="status" {...tid.status}>
      <Tooltip title="Project Status" placement="top">
        <span css={Css.df.aic.gap1.$}>
          <StatusIndicator status={status.code} /> {projectStatusDetails[status.code]}
        </span>
      </Tooltip>
    </span>,
    <span key="stage" {...tid.stage}>
      <Tooltip title="Latest Active Stage" placement="top">
        {stageCodeToNameMapper[latestActiveStage]}
      </Tooltip>
    </span>,
  ].compact();

  const handleUpdateImage = useCallback(
    async ({ contentType, fileName, s3Key, fileSizeInBytes }: PendingAsset) => {
      await updateProjectCoverImage({
        variables: { input: { id: projectId, coverImage: { contentType, fileName, s3Key, fileSizeInBytes } } },
      });
    },
    [updateProjectCoverImage, projectId],
  );

  // Defining some widths for smartly calculating position offsets.
  const imgWidth = 315;
  const imgHeight = 181;
  const gap = 32;

  return (
    <FullBleed omitPadding>
      <div css={Css.bgWhite.df.gapPx(gap).pr5.relative.$}>
        {!sm && (
          <div css={Css.fs0.$}>
            <ImageUploader
              imageUrl={coverImage?.downloadUrl}
              thumbnailUrl={coverImage?.previewUrl ?? undefined}
              onChange={handleUpdateImage}
              xss={Css.objectCover.wPx(imgWidth).hPx(imgHeight).$}
            />
          </div>
        )}

        <div css={Css.w100.df.fdc.mya.$}>
          {!sm && __typename !== "StandaloneProject" && (
            <PageHeaderBreadcrumbs
              breadcrumb={createHierarchyBreadcrumbs(
                { id: projectId, name, parentsRecursive, siblingProjects },
                createProjectDashboardUrl,
                { excludeProject: true },
              )}
              collapsible
            />
          )}
          <div css={Css.df.aic.jcsb.gap2.fg1.pb4.ifMdAndDown.fdc.jcc.aifs.ifSm.py4.px2.$}>
            <div css={Css.df.aic.gap2.fg1.ifMdAndDown.fg0.$}>
              <span css={Css.xl2.lineClamp2.ifMdAndDown.lineClamp1.$} {...tid.address}>
                {name}
              </span>
              {!edit && (
                <ButtonMenu
                  trigger={{ icon: "cog", color: Palette.Gray700 }}
                  items={[
                    {
                      label: "Edit",
                      onClick: () => setQueryParams({ edit: true }, "replaceIn"),
                      disabled: !matchPath({ path: projectDashboardRoute.path, end: true }, location.pathname)
                        ? "Routes to the Dashboard tab to edit the widgets"
                        : false,
                    },
                    ...(lotType === LotType.Bool && propcoUrl
                      ? [{ label: "Propco Platform", onClick: propcoUrl }]
                      : [{ label: "Homeowner Platform", onClick: hopUrl }]),
                    { label: "Project Settings", onClick: createProjectSettingsUrl(projectId) },
                  ]}
                />
              )}
            </div>

            {edit ? (
              <div {...tid.actions} ref={headerActionsRef} />
            ) : (
              <div css={Css.df.gapPx(20).sm.gray800.$}>
                {items.map((item, index) => (
                  <div key={index} css={Css.df.aic.if(index > 0).bl.bw2.bcGray400.plPx(20).$}>
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>

          {!edit && (
            <div css={Css.absolute.bottom0.leftPx(imgWidth + gap).ifSm.left2.$}>
              <Tabs tabs={tabs} data-testid="dashboardTabs" />
            </div>
          )}
        </div>
      </div>
    </FullBleed>
  );
}
